import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Link } from "../components/Link";

import { formatDateTime } from "./admin/shared";

import type { ApiErrorCode } from "../api/client";
import type { ReactNode } from "react";
import type { AdminLive } from "@canvaschool/contracts";

/** Refresh period; a hidden tab skips its turn. */
export const LIVE_REFRESH_MS = 15_000;

/** Pending operations above this mean the room queues are backing up. */
const PENDING_WARNING = 20;

const DIAGNOSTIC_LABEL: Record<string, string> = {
  reconnecting: "재연결",
  version_gap: "버전 건너뜀",
  resync: "재동기화",
  hash_mismatch: "장면 불일치",
  refusal_restore: "편집 되돌림",
  stuck_saving: "저장 지연",
  image_held: "이미지 대기",
  scene_diverged: "장면 어긋남",
  history_stats: "기록 통계",
  ime_stuck: "한영 전환 안 됨",
  deletion_declined: "삭제 거절됨",
  scene_repaired: "부분 복구",
  other: "기타",
};

const METRIC_LABEL: Record<string, string> = {
  mutation_rejects: "편집 거부",
  canvas_resync: "재동기화 요청",
  persistence_failures: "저장 실패",
  asset_failures: "이미지 실패",
  restore_failures: "복원 실패",
  asset_purge_failures: "이미지 정리 실패",
  http_server_errors: "서버 오류",
  login_failures: "로그인 실패",
  client_diagnostics: "화면 진단",
};

/** Diagnostic kinds that mean a student is being blocked or losing work. */
const URGENT_DIAGNOSTICS = new Set([
  "stuck_saving",
  "refusal_restore",
  "image_held",
  "scene_diverged",
  "ime_stuck",
]);

/** Failure counters where a single recent event is worth a look. */
const URGENT_METRICS = new Set([
  "persistence_failures",
  "asset_failures",
  "restore_failures",
  "http_server_errors",
]);

type Signal = AdminLive["signals"][number];

export const signalName = (signal: Signal): string => {
  const base = METRIC_LABEL[signal.metric] ?? signal.metric;
  if (signal.label === null || signal.label === "") {
    return base;
  }
  const detail =
    signal.metric === "client_diagnostics"
      ? DIAGNOSTIC_LABEL[signal.label] ?? signal.label
      : signal.label;
  return `${base} · ${detail}`;
};

/** Thresholds follow scripts/watch-class.sh; rates are per minute of the window. */
export const isSignalWarning = (
  signal: Signal,
  windowSeconds: number,
): boolean => {
  // At least a minute, so a server that just started is not judged on a few seconds.
  const minutes = Math.max(1, windowSeconds / 60);
  if (signal.metric === "mutation_rejects") {
    return (
      (signal.label ?? "").startsWith("FORBIDDEN") &&
      signal.recent > 3 * minutes
    );
  }
  if (URGENT_METRICS.has(signal.metric)) {
    return signal.recent > 0;
  }
  if (signal.metric === "client_diagnostics") {
    if (signal.label === "hash_mismatch") {
      return signal.recent > 10 * minutes;
    }
    return URGENT_DIAGNOSTICS.has(signal.label ?? "") && signal.recent > 0;
  }
  return false;
};

const windowText = (windowSeconds: number): string =>
  windowSeconds < 60
    ? `최근 ${windowSeconds}초`
    : `최근 ${Math.round(windowSeconds / 60)}분`;

/** HH:MM:SS in the browser's time zone. */
const clockText = (date: Date): string =>
  [date.getHours(), date.getMinutes(), date.getSeconds()]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");

const milliseconds = (seconds: number | null): string =>
  seconds === null ? "표본 없음" : `${Math.round(seconds * 1000)} ms`;

const StatCard = ({
  title,
  rows,
}: {
  title: string;
  rows: Array<[string, ReactNode, boolean?]>;
}) => (
  <section className="canvaschool-stat-card" aria-label={title}>
    <h2>{title}</h2>
    <dl>
      {rows.map(([label, value, warn]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd className={warn ? "canvaschool-error" : undefined}>{value}</dd>
        </div>
      ))}
    </dl>
  </section>
);

/**
 * What the server is doing right now, from process memory. Open rooms show the
 * teacher's login id and head counts; class and canvas names never arrive.
 */
export const AdminLivePage = () => {
  const auth = useAuth();
  const { api } = auth;
  const authRef = useRef(auth);
  authRef.current = auth;
  const [live, setLive] = useState<AdminLive | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const loading = useRef(false);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    if (loading.current) {
      return;
    }
    loading.current = true;
    try {
      const next = await api.adminLive();
      if (mounted.current) {
        setLive(next);
        setUpdatedAt(new Date());
        setError(null);
      }
    } catch (failure) {
      // The last good snapshot stays on screen next to the error.
      if (mounted.current) {
        setError(authRef.current.handleError(failure));
      }
    } finally {
      loading.current = false;
    }
  }, [api]);

  useEffect(() => {
    mounted.current = true;
    void load();
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") {
        void load();
      }
    }, LIVE_REFRESH_MS);
    // A tab that comes back into view should not wait for the next tick.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void load();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted.current = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const signals = live
    ? [...live.signals].sort(
        (a, b) =>
          Number(isSignalWarning(b, live.windowSeconds)) -
          Number(isSignalWarning(a, live.windowSeconds)),
      )
    : [];

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>실시간 관제</h1>
          <p className="canvaschool-page-lead">
            지금 서버와 수업 상태입니다. 15초마다 바뀝니다. 학급과 캔버스 이름,
            학생 이름은 표시되지 않습니다.
          </p>
          {updatedAt && (
            <p className="canvaschool-hint">
              마지막 갱신 {clockText(updatedAt)}
            </p>
          )}
        </div>
        <button type="button" onClick={() => void load()}>
          지금 새로고침
        </button>
      </div>
      {error && <ErrorAlert code={error} />}
      {live && (
        <>
          <div className="canvaschool-stat-grid">
            <StatCard
              title="서버"
              rows={[
                [
                  "서버 준비",
                  live.process.ready ? "준비됨" : "준비 안 됨",
                  !live.process.ready,
                ],
                ["서버 시작", formatDateTime(live.process.startedAt)],
                [
                  "메모리",
                  `${Math.round(live.process.rssBytes / 1024 / 1024)} MB`,
                ],
                [
                  "이벤트 루프 지연",
                  `${(live.process.eventLoopLagP99Seconds * 1000).toFixed(
                    1,
                  )} ms`,
                ],
              ]}
            />
            <StatCard
              title="접속"
              rows={[
                ["접속 소켓", live.realtime.connections],
                ["편집 중", live.realtime.editors],
                ["열린 방", live.realtime.rooms],
                [
                  "대기 작업",
                  live.realtime.pendingOperations,
                  live.realtime.pendingOperations > PENDING_WARNING,
                ],
              ]}
            />
            <StatCard
              title="지연"
              rows={[
                [
                  "방 반영 p95",
                  milliseconds(live.latency.roomUpdateP95Seconds),
                ],
                [
                  "기록 저장 p95",
                  milliseconds(live.latency.operationLogCommitP95Seconds),
                ],
              ]}
            />
          </div>

          <section aria-label="신호" className="canvaschool-todo">
            <h2>신호</h2>
            <div className="canvaschool-table-wrap">
              <table className="canvaschool-table">
                <thead>
                  <tr>
                    <th scope="col">항목</th>
                    <th scope="col">{windowText(live.windowSeconds)}</th>
                    <th scope="col">시작 후 누적</th>
                  </tr>
                </thead>
                <tbody>
                  {signals.map((signal) => {
                    const warn = isSignalWarning(signal, live.windowSeconds);
                    return (
                      <tr key={signal.key}>
                        <td className={warn ? "canvaschool-error" : undefined}>
                          {signalName(signal)}
                        </td>
                        <td
                          className={
                            warn ? "canvaschool-error" : "canvaschool-hint"
                          }
                        >
                          {signal.recent}
                        </td>
                        <td className="canvaschool-hint">{signal.total}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section aria-label="제보" className="canvaschool-todo">
            <h2>제보</h2>
            <div className="canvaschool-table-wrap">
              <table className="canvaschool-table">
                <thead>
                  <tr>
                    <th scope="col">기간</th>
                    <th scope="col">오류</th>
                    <th scope="col">제보</th>
                    <th scope="col">설문</th>
                    <th scope="col">진단</th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ["최근 10분", live.reports.last10m],
                      ["최근 1시간", live.reports.last1h],
                    ] as const
                  ).map(([label, counts]) => (
                    <tr key={label}>
                      <th scope="row">{label}</th>
                      <td>{counts.error}</td>
                      <td>{counts.userReport}</td>
                      <td>{counts.survey}</td>
                      <td>{counts.diagnostic}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="canvaschool-hint">
              <Link to="/admin/reports">제보 목록 보기</Link>
            </p>
          </section>

          <section aria-label="수업 방" className="canvaschool-todo">
            <h2>수업 방</h2>
            {live.rooms.length === 0 ? (
              <p className="canvaschool-hint">열린 방이 없습니다.</p>
            ) : (
              <div className="canvaschool-table-wrap">
                <table className="canvaschool-table">
                  <thead>
                    <tr>
                      <th scope="col">교사 아이디</th>
                      <th scope="col">교사 접속</th>
                      <th scope="col">학생(활동 중)</th>
                      <th scope="col">손든 학생</th>
                    </tr>
                  </thead>
                  <tbody>
                    {live.rooms.map((room, index) => (
                      <tr key={index}>
                        <td>{room.teacherLoginId ?? "—"}</td>
                        <td>{room.teacherPresent ? "접속 중" : "없음"}</td>
                        <td>
                          {room.students} ({room.activeStudents})
                        </td>
                        <td>{room.handsRaised}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </section>
  );
};
